import { useCallback, useEffect, useRef } from "react";
import type { SandboxErrorReason } from "@/lib/api";
import { ConfirmButton } from "@/components/common/ConfirmButton";

interface EnvironmentErrorDialogProps {
	open: boolean;
	/** 異常の原因。未設定の場合は汎用文言を表示する。 */
	errorReason?: SandboxErrorReason;
	/** 起動の失敗の具体的な説明。deviceAccessDenied のときは理由ごとの文の代わりに出す。 */
	failureDetail?: string | null;
	onClose: () => void;
}

const REASON_TEXT: Record<SandboxErrorReason, { title: string; description: string }> = {
	outOfMemory: {
		title: "メモリ上限を超過しました",
		description:
			"エージェント内の処理がメモリ上限を超えたため、コンテナが強制終了されました。処理のメモリ使用量を減らして再度実行してください。繰り返し発生する場合は管理者に連絡してください。",
	},
	unschedulable: {
		title: "キャパシティが不足しています",
		description:
			"現在このエージェントを起動できる空きがありません。稼働中のエージェントを停止するか、しばらく待ってから再度起動してください。繰り返し発生する場合は管理者に連絡してください。",
	},
	capacityUnavailable: {
		title: "インスタンスを確保できませんでした",
		description:
			"選択したインスタンスサイズを現在確保できません。別のサイズを選んで起動するか、しばらく待ってから再度起動してください。",
	},
	diskLimitExceeded: {
		title: "エージェントのディスク上限に達しています",
		description:
			"エージェントのディスク使用量が60 GiB以上のため起動できません。使用量を減らしてから再度起動してください。",
	},
	diskSpaceInsufficient: {
		title: "保存先の空き容量が不足しています",
		description:
			"保存先ディスクの空き容量が10 GiB未満のため起動できません。空き容量を確保してから再度起動してください。",
	},
	deviceAccessDenied: {
		title: "USB 機器を開けませんでした",
		description:
			"選んだ USB 機器を使えないため、エージェントを起動できませんでした。機器がデバイスに差してあるか確かめて、エージェントを起動し直してください。",
	},
	volumeExpansionRejected: {
		title: "ボリュームを拡張できません",
		description:
			"このクラスタではボリュームを拡張できないため、拡張後の容量でエージェントを起動できません。管理者に連絡してください。",
	},
	containerUnavailable: {
		title: "コンテナが繰り返し異常終了しています",
		description:
			"エージェントのコンテナを正常に起動できませんでした。時間をおいて再度起動するか、管理者に連絡してください。",
	},
};

/** 環境コンテナの起動失敗の理由を表示するダイアログ。 */
export function EnvironmentErrorDialog({
	open,
	errorReason,
	failureDetail,
	onClose,
}: EnvironmentErrorDialogProps) {
	const dialogRef = useRef<HTMLDialogElement>(null);

	useEffect(() => {
		const el = dialogRef.current;
		if (!el) return;
		if (open && !el.open) {
			el.showModal();
		} else if (!open && el.open) {
			el.close();
		}
	}, [open]);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === "Escape") {
				onClose();
				e.preventDefault();
			}
		},
		[onClose],
	);

	if (!open) return null;

	const text = errorReason
		? {
				title: REASON_TEXT[errorReason].title,
				description:
					errorReason === "deviceAccessDenied" && failureDetail
						? failureDetail
						: REASON_TEXT[errorReason].description,
			}
		: {
				title: "エージェントの起動に失敗しました",
				description: "エージェントのコンテナの起動時にエラーが発生しました。",
			};

	return (
		<dialog
			ref={dialogRef}
			onKeyDown={handleKeyDown}
			data-testid="environment-error-dialog"
			style={{
				margin: "auto",
				padding: 0,
				border: "none",
				borderRadius: "var(--radius-4)",
				boxShadow: "var(--shadow-card)",
				background: "var(--surface-1)",
				width: 520,
			}}
			className="backdrop:bg-scrim"
		>
			<div style={{ padding: "20px 24px 16px", borderBottom: "var(--pane-header-border)" }}>
				<h3
					style={{
						margin: 0,
						fontSize: "var(--text-title)",
						fontWeight: 600,
						color: "var(--fg-1)",
						fontFamily: "var(--font-sans)",
					}}
				>
					{text.title}
				</h3>
			</div>
			<div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 12 }}>
				<span
					style={{
						fontSize: "var(--text-caption)",
						color: "var(--fg-2)",
						fontFamily: "var(--font-sans)",
					}}
				>
					{text.description}
				</span>
			</div>
			<div
				style={{
					display: "flex",
					justifyContent: "flex-end",
					padding: "12px 24px",
					borderTop: "var(--pane-header-border)",
				}}
			>
				<ConfirmButton
					label="閉じる"
					variant="ghost"
					loading={false}
					onClick={onClose}
					data-testid="environment-error-close"
				/>
			</div>
		</dialog>
	);
}
